/**
 * 公开展示站 — the recruitment surface.
 *
 * Mode: Persuade. The visitor is a student deciding whether to join. They must
 * understand what this club actually builds, believe it is real, and act — all
 * inside the first two viewports.
 *
 * The board layer is the thesis: this is a powered circuit board, and the page's
 * job is to show current flowing through it. Everything the canvas draws is also
 * a number on the page, so a WebGL failure costs atmosphere and nothing else.
 */

import { Suspense, lazy, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type Department, type Health, type PublicContent } from "../../lib/api.ts";

import { ThemeToggle } from "../../components/ThemeScript.tsx";
// three.js is ~528 kB and only the showcase needs it. Loading it on demand
// keeps it out of the admin and member bundles entirely, which is the whole
// reason the code-split boundary sits here.
const BoardField = lazy(() =>
  import("../../components/BoardField.tsx").then((module) => ({ default: module.BoardField }))
);

import {
  BandStrip,
  DesignatorTag,
  Empty,
  Figure,
  SectionTitle,
  designator,
  statusLabel,
  type Designator,
} from "../../components/kit.tsx";

/** Departments get stable位号 in display order, so a colour band never moves. */
const DEPT_PREFIX: Designator[] = ["U", "R", "J", "C", "L", "F", "Y", "D"];

export function Showcase() {
  const [content, setContent] = useState<PublicContent | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [health, setHealth] = useState<Health | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // All three are independent; a slow health check must not delay the page.
    void api.get<PublicContent>("/api/public/content").then(setContent).catch(() => setFailed(true));
    void api.get<Department[]>("/api/public/departments").then(setDepartments).catch(() => undefined);
    void api.get<Health>("/api/health").then(setHealth).catch(() => undefined);
  }, []);

  const projects = content?.projects ?? [];
  const achievements = content?.achievements ?? [];
  const settings = content?.settings;

  // Real numbers only. When the club has not entered data yet the count is
  // genuinely zero and the copy says so — inventing a project total to make the
  // page look finished is exactly the failure this system is built to avoid.
  // Projects have no status field; the showcase's own content is the count.
  const totalCount = departments.length + projects.length;
  const liveCount = projects.filter((project) => project.visibility === "public").length;
  const hasContent = totalCount > 0;

  return (
    <div className="min-h-screen">
      <a href="#main" className="sr-only focus:not-sr-only">
        跳到主要内容
      </a>

      <SiteHeader />

      <main id="main">
        {/* ------------------------------------------------------- 首屏 ---- */}
        <section className="border-b border-line" aria-labelledby="hero-title">
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-px px-6 md:px-8 lg:grid-cols-12">
            {/* 7 列：主张与行动 */}
            <div className="py-14 lg:col-span-7 lg:border-r lg:border-line lg:py-24 lg:pr-14">
              <p className="silk-label mb-8 flex items-center gap-3 text-[10px] text-copper">
                <BandStrip seed="kc" />
                科创中心 · TECH SYNERGY LAB
              </p>

              <h1 id="hero-title" className="silk text-[clamp(2.75rem,7.2vw,5.75rem)]">
                把想法做成
                <br />
                <span className="text-signal">能跑的东西</span>
              </h1>

              <p className="mt-8 max-w-[46ch] text-[17px] leading-relaxed text-silk-dim">
                {settings?.heroDescription ||
                  "软件与硬件的协同。我们从真实问题出发，做原型、集成、测试，把能运行的作品交出来。"}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/join"
                  className="silk-label border border-signal bg-signal px-6 py-3.5 text-[13px] text-silk-inverse transition-colors hover:bg-signal/90 active:translate-y-px"
                >
                  领用你的第一个元件
                </Link>
                <a
                  href="#projects"
                  className="silk-label border border-line-strong px-6 py-3.5 text-[13px] text-silk transition-colors hover:bg-silk hover:text-silk-inverse active:translate-y-px"
                >
                  看我们在做什么
                </a>
              </div>

              {/* 真实计数 */}
              <dl className="mt-16 grid max-w-lg grid-cols-3 gap-6 border-t border-line pt-8">
                <Figure value={departments.length} caption="在册部门" tone="copper" />
                <Figure value={liveCount} caption="通电项目" tone="signal" />
                <Figure value={achievements.length} caption="已交付成果" />
              </dl>

              {!hasContent && !failed ? (
                <p className="mt-6 max-w-[52ch] text-[12px] leading-relaxed text-silk-dim/80">
                  当前数据库尚未录入部门与项目，因此上面的计数是零。页面不会用占位数字填充它——
                  这是社团系统的一条硬规则：没有的数据就显示没有。
                </p>
              ) : null}
            </div>

            {/* 5 列：板层 */}
            <div className="relative flex flex-col justify-end pb-14 lg:col-span-5 lg:py-24 lg:pl-14">
              <Suspense
                fallback={
                  // Reserve the exact space the canvas will take, so the first
                  // viewport does not shift when the engine arrives.
                  <div
                    aria-hidden
                    className="h-[46vh] min-h-[320px] w-full lg:h-[560px]"
                    style={{
                      backgroundImage:
                        "linear-gradient(to right, var(--graticule) 1px, transparent 1px), linear-gradient(to bottom, var(--graticule) 1px, transparent 1px)",
                      backgroundSize: "48px 48px",
                    }}
                  />
                }
              >
                <BoardField
                  liveCount={liveCount}
                  totalCount={Math.max(totalCount, 8)}
                  className="h-[46vh] min-h-[320px] w-full lg:h-[560px]"
                />
              </Suspense>

              {/* 元件标签：贴板下沿的一条真实标签 */}
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-5">
                <DesignatorTag code={designator("R", 1)} label="软件 · 在线" />
                <DesignatorTag code={designator("U", 7)} label="硬件 · 在线" />
                <DesignatorTag code={designator("J", 2)} label="招新入口 · 常开" />
              </div>

              <p className="data mt-4 text-[11px] text-silk-dim/70">
                {health
                  ? `板层在线 · ${health.database} · 项目工具 ${health.projectProvider}`
                  : "板层连接中"}
              </p>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------- 部门 ---- */}
        <Section
          id="departments"
          label="DEPARTMENTS / 部门"
          title="按方向认领位置"
          note="每个部门是一个可领取的模块。加入时你会知道自己负责哪一块。"
        >
          {departments.length === 0 ? (
            <Empty
              title="部门尚未录入"
              detail="管理员在后台添加部门后，这里会按位号顺序排列，并显示各自的开放状态。"
            />
          ) : (
            <ul className="grid grid-cols-1 gap-px border border-line bg-[var(--line)] md:grid-cols-2 xl:grid-cols-4">
              {departments.map((department, index) => (
                <li key={department.id} className="flex flex-col gap-5 bg-ground p-6">
                  <div className="flex items-start justify-between gap-3">
                    <DesignatorTag
                      code={designator(DEPT_PREFIX[index % DEPT_PREFIX.length]!, index + 1)}
                      label={department.isOpen ? "招募中" : "已满员"}
                    />
                    <BandStrip seed={department.id} />
                  </div>
                  <div>
                    <h3 className="text-[17px] text-silk">{department.name}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-silk-dim">
                      {department.description || "暂无描述"}
                    </p>
                  </div>
                  <span className="data mt-auto text-[11px] text-copper">{department.id}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* ------------------------------------------------------- 项目 ---- */}
        <Section
          id="projects"
          label="PROJECTS / 项目"
          title="在跑的东西"
          note="公开项目。内部与私有项目不在这里出现——那是俱乐部自己的判断。"
        >
          {projects.length === 0 ? (
            <Empty
              title="还没有公开项目"
              detail="第一个项目录入后，这里会用它真实的项目图、状态与成员表展示，不用占位图冒充。"
            />
          ) : (
            <ul className="grid grid-cols-1 gap-px border border-line bg-[var(--line)] lg:grid-cols-2">
              {projects.map((project, index) => (
                <li key={project.id} className="group flex flex-col gap-5 bg-ground p-6 lg:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <DesignatorTag
                      code={designator("U", index + 1)}
                      label={statusLabel(project.visibility)}
                    />
                    {project.posterUrl ? (
                      <img
                        src={project.posterUrl}
                        alt={`${project.title} 的项目图`}
                        className="h-20 w-28 border border-line object-cover"
                        loading="lazy"
                      />
                    ) : null}
                  </div>
                  <h3 className="text-[20px] leading-snug text-silk">{project.title}</h3>
                  <p className="text-[14px] leading-relaxed text-silk-dim">
                    {project.description || "暂无描述"}
                  </p>
                  {project.tags.length > 0 ? (
                    <ul className="flex flex-wrap gap-2">
                      {project.tags.map((tag) => (
                        <li
                          key={tag}
                          className="silk-label border border-line px-2 py-1 text-[10px] text-silk-dim"
                        >
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {project.links.length > 0 ? (
                    <ul className="mt-auto flex flex-wrap gap-3">
                      {project.links.map((link) => (
                        <li key={link.label}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noreferrer"
                            className="data text-[11px] text-copper hover:underline"
                          >
                            {link.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* ------------------------------------------------------- 成果 ---- */}
        <Section
          label="DELIVERABLES / 成果"
          title="交出来的东西"
          note="只记录可核验的成果。没有记录就是没有记录，这里不用形容词填空。"
        >
          {achievements.length === 0 ? (
            <Empty
              title="成果档案待录入"
              detail="竞赛与成果资料需要逐项核验后录入。系统不允许自动生成奖项或名次。"
            />
          ) : (
            <ul className="grid grid-cols-1 gap-px border border-line bg-[var(--line)] md:grid-cols-3">
              {achievements.map((achievement, index) => (
                <li key={achievement.id} className="flex flex-col gap-4 bg-ground p-6">
                  <DesignatorTag code={designator("Y", index + 1)} label={achievement.achievedOn?.slice(0, 4)} />
                  <h3 className="text-[16px] leading-snug text-silk">{achievement.title}</h3>
                  <p className="text-[13px] leading-relaxed text-silk-dim">{achievement.description}</p>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* ------------------------------------------------------- 流程 ---- */}
        <JoinProcess />
      </main>

      <SiteFooter settings={settings} />
    </div>
  );
}

function Section({
  id,
  label,
  title,
  note,
  children,
}: {
  id?: string;
  label: string;
  title: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="border-b border-line py-16 lg:py-24">
      <div className="mx-auto max-w-[1440px] px-6 md:px-8">
        <div className="mb-10 flex flex-col gap-4 lg:mb-14 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-col gap-4">
            <SectionTitle>{label}</SectionTitle>
            <h2 className="silk text-[clamp(1.75rem,3.4vw,2.75rem)]">{title}</h2>
          </div>
          <p className="max-w-[42ch] text-[13px] leading-relaxed text-silk-dim">{note}</p>
        </div>
        {children}
      </div>
    </section>
  );
}

/**
 * 加入流程.
 *
 * The club's real life is paperwork — a requisition form, a signature, a part
 * handed over. The join path is a 领料单 for exactly that reason, and because
 * the metaphor is literal, the first step is a real form that posts to the API.
 */
function JoinProcess() {
  return (
    <section className="py-16 lg:py-24">
      <div className="mx-auto max-w-[1440px] px-6 md:px-8">
        <div className="mb-12 flex flex-col gap-4">
          <SectionTitle>ONBOARDING / 加入流程</SectionTitle>
          <h2 className="silk text-[clamp(1.75rem,3.4vw,2.75rem)]">领用你的第一个元件</h2>
        </div>

        <ol className="grid grid-cols-1 gap-px border border-line bg-[var(--line)] md:grid-cols-4">
          {[
            { code: "J1", name: "提交领料单", detail: "填姓名、学号、班级、意向部门。线上一次提交，不需要跑一趟。" },
            { code: "J2", name: "部门面谈", detail: "负责人和你聊一次，确认方向，也确认你能接住这个方向。" },
            { code: "J3", name: "领取账号", detail: "管理员在后台创建账号并发一次性激活码，你自行设置密码。" },
            { code: "J4", name: "领到第一个零件", detail: "从器材柜领到你的第一件物料，记在库存台账上。" },
          ].map((step, index) => (
            <li key={step.code} className="relative flex flex-col gap-4 bg-ground p-6">
              <div className="flex items-center justify-between gap-3">
                <DesignatorTag code={step.code} label={`步骤 ${index + 1}`} />
                {index < 3 ? (
                  <span aria-hidden className="data text-[11px] text-copper">
                    ↓
                  </span>
                ) : null}
              </div>
              <h3 className="text-[16px] text-silk">{step.name}</h3>
              <p className="text-[13px] leading-relaxed text-silk-dim">{step.detail}</p>
            </li>
          ))}
        </ol>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Link
            to="/join"
            className="silk-label border border-signal bg-signal px-6 py-3.5 text-[13px] text-silk-inverse transition-colors hover:bg-signal/90 active:translate-y-px"
          >
            开始申请
          </Link>
          <span className="text-[12px] text-silk-dim">
            表单有防机器人字段与频率限制，同一位申请者三次以内可以反复修正。
          </span>
        </div>
      </div>
    </section>
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-30 border-b border-line bg-[color-mix(in_srgb,var(--ground)_88%,transparent)] backdrop-blur-md">
      <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-6 py-4 md:px-8">
        <Link to="/" className="flex items-baseline gap-3">
          <span aria-hidden className="block h-3.5 w-3.5 border border-copper" />
          <span className="silk text-[15px]">科创中心</span>
          <span className="silk-label hidden text-[9px] text-silk-dim sm:inline">TECH SYNERGY LAB</span>
        </Link>

        <nav aria-label="主导航" className="flex items-center gap-5">
          <a href="/#departments" className="silk-label hidden text-[10px] text-silk-dim hover:text-silk md:inline">
            部门
          </a>
          <a href="/#projects" className="silk-label hidden text-[10px] text-silk-dim hover:text-silk md:inline">
            项目
          </a>
          <a href="/#deliverables" className="silk-label hidden text-[10px] text-silk-dim hover:text-silk md:inline">
            成果
          </a>
          <ThemeToggle />
          <Link
            to="/sign-in"
            className="silk-label border border-line-strong px-3 py-1.5 text-[10px] text-silk transition-colors hover:bg-silk hover:text-silk-inverse"
          >
            登录
          </Link>
        </nav>
      </div>
    </header>
  );
}

function SiteFooter({ settings }: { settings: PublicContent["settings"] | undefined }) {
  return (
    <footer className="border-t border-line py-12">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-8 px-6 md:px-8 lg:flex-row lg:justify-between">
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline gap-3">
            <span aria-hidden className="block h-3 w-3 border border-copper" />
            <span className="silk text-[14px]">科创中心</span>
          </div>
          <p className="data text-[11px] text-silk-dim">TECH SYNERGY LAB</p>
          {settings?.contactEmail ? (
            <a href={`mailto:${settings.contactEmail}`} className="data text-[12px] text-copper hover:underline">
              {settings.contactEmail}
            </a>
          ) : (
            <p className="text-[12px] text-silk-dim">联系邮箱待录入</p>
          )}
        </div>

        <div className="flex flex-col gap-3 lg:items-end">
          <p className="max-w-[40ch] text-[12px] leading-relaxed text-silk-dim lg:text-right">
            管理系统与本站共用同一套颜色与设计系统。
          </p>
          <div className="flex gap-5">
            <Link to="/sign-in" className="silk-label text-[10px] text-silk-dim hover:text-silk">
              成员登录
            </Link>
            <Link to="/sign-in/admin" className="silk-label text-[10px] text-silk-dim hover:text-silk">
              管理入口
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
